import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CardViewMode, StageBlueprint, Venue } from '../../types';
import { ConicGlowCard } from '../common/ConicGlowCard';
import { runStageViewTransition } from '../../utils/viewTransitions';
import {
  MapPinIcon,
  UsersIcon,
  StarIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  SparklesIcon,
  ArrowRightIcon,
  CloseIcon,
  FlameIcon
} from '../common/Icons';

interface SpotlightGridDeckProps {
  venues: Venue[];
  allVenues?: Venue[];
  initialCorridor?: string;
  searchFilter?: any;
  cardView?: CardViewMode;
  focusMetrics?: string[];
  voiceSpotlightVenueId?: string | null;
  blueprint?: StageBlueprint | null;
  relaxationNotice?: string | null;
  onSelectVenue: (venue: Venue, initialFocusView?: 'overview' | 'packages' | 'photos' | 'amenities') => void;
  onCompareVenue?: (venue?: Venue) => void;
  onCheckAvailability?: (venue: Venue) => void;
  onSelectCorridor?: (corridor: string) => void;
  onClearVoiceFilter?: () => void;
  onTouchAction?: (action: string, data?: any) => void;
}

export const SpotlightGridDeck: React.FC<SpotlightGridDeckProps> = React.memo(({
  venues,
  allVenues,
  initialCorridor = 'All',
  searchFilter,
  cardView,
  focusMetrics: propFocusMetrics,
  voiceSpotlightVenueId = null,
  blueprint,
  relaxationNotice,
  onSelectVenue,
  onCompareVenue,
  onSelectCorridor,
  onClearVoiceFilter,
  onTouchAction
}) => {
  const [selectedCorridors, setSelectedCorridors] = useState<string[]>(
    initialCorridor && initialCorridor !== 'All' ? [initialCorridor] : []
  );
  const [selectedVenueTypes, setSelectedVenueTypes] = useState<string[]>([]);
  const [activeCardView, setActiveCardView] = useState<CardViewMode>(cardView || 'discovery');
  const [minGuestsFilter, setMinGuestsFilter] = useState<number>(0);
  const sortMode = 'popular_gps';
  const [userModifiedFilter, setUserModifiedFilter] = useState<boolean>(false);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1440
  );
  const [renderedCount, setRenderedCount] = useState<number>(16);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number; isLiveGps: boolean }>({
    lat: 0,
    lng: 0,
    isLiveGps: false
  });
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledSpotlightIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (Array.isArray(searchFilter?.areas) && searchFilter.areas.length > 0) {
      setSelectedCorridors(searchFilter.areas);
    } else if (searchFilter?.area && searchFilter.area !== 'All' && searchFilter.area !== 'any') {
      setSelectedCorridors([searchFilter.area]);
    } else if (initialCorridor && initialCorridor !== 'All') {
      setSelectedCorridors([initialCorridor]);
    } else {
      setSelectedCorridors([]);
    }

    if (Array.isArray(searchFilter?.venueTypes) && searchFilter.venueTypes.length > 0) {
      setSelectedVenueTypes(searchFilter.venueTypes);
    } else if (searchFilter?.venueType && searchFilter.venueType !== 'any' && searchFilter.venueType !== 'all') {
      setSelectedVenueTypes([searchFilter.venueType]);
    } else {
      setSelectedVenueTypes([]);
    }

    if (searchFilter?.minGuests) {
      setMinGuestsFilter(Math.min(Number(searchFilter.minGuests), 1500));
    } else {
      setMinGuestsFilter(0);
    }
    setUserModifiedFilter(false);
    setRenderedCount(16);
  }, [initialCorridor, searchFilter]);

  useEffect(() => {
    if (cardView) {
      setActiveCardView(cardView);
    } else if (searchFilter?.cardView) {
      setActiveCardView(searchFilter.cardView);
    } else {
      setActiveCardView('discovery');
    }
  }, [cardView, searchFilter?.cardView]);

  // Smoothly glide mobile horizontal scroller once when Leda spotlights a new card
  useEffect(() => {
    if (!voiceSpotlightVenueId) {
      lastScrolledSpotlightIdRef.current = null;
      return;
    }
    if (lastScrolledSpotlightIdRef.current === voiceSpotlightVenueId) return;
    const targetNode = cardRefs.current[voiceSpotlightVenueId];
    if (targetNode && typeof targetNode.scrollIntoView === 'function' && viewportWidth <= 899) {
      lastScrolledSpotlightIdRef.current = voiceSpotlightVenueId;
      try {
        targetNode.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      } catch {}
    }
  }, [voiceSpotlightVenueId, viewportWidth, renderedCount]);

  const requestLiveGps = () => {
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserCoords({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            isLiveGps: true
          });
        },
        () => {},
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
      );
    }
  };

  const getDistanceKm = (lat1: number, lng1: number, lat2?: number, lng2?: number): number | null => {
    if (!userCoords.isLiveGps || !lat2 || !lng2) return null;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.max(0.1, Math.round(R * c * 10) / 10);
  };

  const fallbackVenues: Venue[] = [
    {
      id: '923dc479-0831-4287-a03d-e65b290f459a',
      slug: 'm-venpick-karachi-saddar',
      name: 'Mövenpick Karachi',
      area: 'DHA/Clifton',
      address: 'Club Rd, Civil Lines Karachi, 75530, Pakistan',
      lat: 24.8467956,
      lng: 67.0262067,
      venueType: 'hotel_ballroom',
      minCapacity: 200,
      maxCapacity: 1050,
      googleRating: 4.5,
      googleReviewCount: 25649,
      isClaimed: false,
      amenities: ['Pillarless Ballroom', 'Central Chiller AC', 'Standby Generator Backup', 'Valet Parking'],
      images: [
        {
          id: 'img-movenpick-0',
          venueId: '923dc479-0831-4287-a03d-e65b290f459a',
          source: 'google_maps',
          filePathWebp: '/uploads/venues/m-venpick-karachi-saddar/photo_0.webp',
          filePathAvif: '/uploads/venues/m-venpick-karachi-saddar/photo_0.avif',
          category: 'hall',
          isCover: true,
          isHidden: false,
          sortOrder: 0
        }
      ],
      packages: [
        { id: '978af272-468c-422d-9ae7-89a8a8cc1254', packageType: 'flat_rental_offpeak', name: 'Estimated Flat Hall Rental (Off-Peak)', pricePkr: 370000, minGuests: 200, menuItems: ['Main Ballroom', 'Central Chiller AC', 'Standby Generator Backup', 'Valet Parking'] },
        { id: '54066496-11c8-4031-90fb-5026e0418143', packageType: 'flat_rental_peak', name: 'Estimated Flat Hall Rental (Peak Season)', pricePkr: 560000, minGuests: 200, menuItems: ['Main Ballroom', 'Central Chiller AC', 'Standby Generator Backup', 'Valet Parking'] }
      ]
    },
    {
      id: 'e045ec1b-c3e3-4de2-9f46-bc9a03ef5de6',
      slug: 'defence-authority-creek-club-dha',
      name: 'Defence Authority Creek Club',
      area: 'DHA/Clifton',
      address: 'Q3XH+2JC, DHA Karachi Phase VIII Zone A Zone A Phase 8 Defence Housing Authority, Karachi, 75500, Pakistan',
      lat: 24.7975533,
      lng: 67.0790219,
      venueType: 'lawn',
      minCapacity: 300,
      maxCapacity: 1300,
      googleRating: 4.3,
      googleReviewCount: 4823,
      isClaimed: false,
      amenities: ['Waterfront Open Lawn', 'Standby Generator Backup', 'Valet Parking', 'Bridal Suite'],
      images: [
        {
          id: 'img-creek-0',
          venueId: 'e045ec1b-c3e3-4de2-9f46-bc9a03ef5de6',
          source: 'google_maps',
          filePathWebp: '/uploads/venues/defence-authority-creek-club-dha/photo_0.webp',
          filePathAvif: '/uploads/venues/defence-authority-creek-club-dha/photo_0.avif',
          category: 'lawn',
          isCover: true,
          isHidden: false,
          sortOrder: 0
        }
      ],
      packages: [
        { id: '1176fde6-6d7e-4c3c-a300-e5f721c10312', packageType: 'flat_rental_offpeak', name: 'Estimated Flat Hall Rental (Off-Peak)', pricePkr: 470000, minGuests: 300, menuItems: ['Waterfront Open Lawn', 'Standby Generator Backup', 'Valet Parking'] },
        { id: '3419a77c-c516-43ba-96cc-ba1b4c832967', packageType: 'flat_rental_peak', name: 'Estimated Flat Hall Rental (Peak Season)', pricePkr: 730000, minGuests: 300, menuItems: ['Waterfront Open Lawn', 'Standby Generator Backup', 'Valet Parking'] }
      ]
    },
    {
      id: '184f5362-56b0-43db-be0e-aa02c8f789cc',
      slug: 'four-seasons-banquet-hall-gulistan',
      name: 'Four Seasons Banquet Hall',
      area: 'Gulshan-e-Iqbal',
      address: 'WR8J+Q9G, Main Rashid Minhas Rd, Block 5 Gulshan-e-Iqbal, Karachi, Pakistan',
      lat: 24.9169313,
      lng: 67.0815152,
      venueType: 'banquet_hall',
      minCapacity: 150,
      maxCapacity: 950,
      googleRating: 4.4,
      googleReviewCount: 4556,
      isClaimed: false,
      amenities: ['Central Chiller AC', 'Standby Generator Backup', 'Valet Parking', 'Bridal Suite'],
      images: [
        {
          id: 'img-fourseasons-0',
          venueId: '184f5362-56b0-43db-be0e-aa02c8f789cc',
          source: 'google_maps',
          filePathWebp: '/uploads/venues/four-seasons-banquet-hall-gulistan/photo_0.webp',
          filePathAvif: '/uploads/venues/four-seasons-banquet-hall-gulistan/photo_0.avif',
          category: 'hall',
          isCover: true,
          isHidden: false,
          sortOrder: 0
        }
      ],
      packages: [
        { id: 'p4-flat-offpeak', packageType: 'flat_rental_offpeak', name: 'Estimated Flat Hall Rental (Off-Peak)', pricePkr: 290000, minGuests: 150, menuItems: ['Central Chiller AC', 'Standby Generator Backup', 'Valet Parking'] },
        { id: 'p4-flat', packageType: 'flat_rental_peak', name: 'Estimated Flat Hall Rental (Peak Season)', pricePkr: 510000, minGuests: 150, menuItems: ['Central Chiller AC', 'Standby Generator Backup', 'Valet Parking'] }
      ]
    },
    {
      id: 'a6f6df01-334a-40e5-82bc-406a8a78cb81',
      slug: 'sadabahar-marquee-karsaz',
      name: 'Sadabahar Marquee',
      area: 'Tariq Road',
      address: '53/54, Kokan Society, Hyder Ali Road, Alamgir Rd, Karachi, 74800, Pakistan',
      lat: 24.8789824,
      lng: 67.0732063,
      venueType: 'marquee',
      minCapacity: 250,
      maxCapacity: 1200,
      googleRating: 4.6,
      googleReviewCount: 3825,
      isClaimed: false,
      amenities: ['Central Chiller AC', 'Standby Generator Backup', 'Valet Parking'],
      images: [
        {
          id: 'img-sadabahar-0',
          venueId: 'a6f6df01-334a-40e5-82bc-406a8a78cb81',
          source: 'google_maps',
          filePathWebp: '/uploads/venues/sadabahar-marquee-karsaz/photo_0.webp',
          filePathAvif: '/uploads/venues/sadabahar-marquee-karsaz/photo_0.avif',
          category: 'hall',
          isCover: true,
          isHidden: false,
          sortOrder: 0
        }
      ],
      packages: [
        { id: 'p5-flat-offpeak', packageType: 'flat_rental_offpeak', name: 'Estimated Flat Hall Rental (Off-Peak)', pricePkr: 320000, minGuests: 250, menuItems: ['Central Chiller AC', 'Standby Generator Backup', 'Valet Parking'] },
        { id: 'p5-flat', packageType: 'flat_rental_peak', name: 'Estimated Flat Hall Rental (Peak Season)', pricePkr: 490000, minGuests: 250, menuItems: ['Central Chiller AC', 'Standby Generator Backup', 'Valet Parking'] }
      ]
    }
  ];

  const baseVenues = venues.length > 0 ? venues : fallbackVenues;
  const hasVoiceSearchCriteria = Boolean(
    searchFilter?.query ||
      searchFilter?.venueName ||
      searchFilter?.minGuests ||
      searchFilter?.maxGuests ||
      searchFilter?.maxBudgetPerHead ||
      searchFilter?.maxFlatRental ||
      searchFilter?.sortBy ||
      (Array.isArray(searchFilter?.amenities) && searchFilter.amenities.length > 0) ||
      typeof searchFilter?.isRelaxedMatch === 'boolean'
  );
  const hasOnlyCorridorFilter =
    selectedCorridors.length > 0 &&
    selectedVenueTypes.length === 0 &&
    minGuestsFilter === 0 &&
    !searchFilter?.query &&
    !searchFilter?.venueName &&
    !searchFilter?.venueType &&
    (!Array.isArray(searchFilter?.venueTypes) || searchFilter.venueTypes.length === 0) &&
    !searchFilter?.minGuests &&
    !searchFilter?.maxBudgetPerHead &&
    !searchFilter?.maxFlatRental;

  const sourceVenues =
    (userModifiedFilter || (hasOnlyCorridorFilter && !hasVoiceSearchCriteria && allVenues && allVenues.length > baseVenues.length)) &&
    allVenues &&
    allVenues.length > 0
      ? allVenues
      : baseVenues;

  const isBackendFiltered = Boolean(
    !userModifiedFilter &&
      searchFilter &&
      (hasVoiceSearchCriteria ||
        (!hasOnlyCorridorFilter &&
          ((Array.isArray(searchFilter.areas) && searchFilter.areas.length > 0) ||
            (Array.isArray(searchFilter.venueTypes) && searchFilter.venueTypes.length > 0))))
  );

  const getVenuePeakHallRental = (v: Venue): { peakPkr: number; offPeakPkr: number; isEstimated: boolean } => {
    const uniquePkgs = Array.from(
      new Map((v.packages || []).map((p) => [p.name, p])).values()
    );
    const sortedPkgs = [...uniquePkgs].sort((a, b) => a.pricePkr - b.pricePkr);
    const perHeadPkgs = sortedPkgs.filter(
      (p) => !p.packageType?.includes('flat_rental') && p.pricePkr < 25000
    );
    const minPerHead = perHeadPkgs.length > 0 ? perHeadPkgs[0].pricePkr : 1850;
    const flatPeak =
      uniquePkgs.find(
        (p) => !p.packageType?.includes('offpeak') && (p.packageType?.includes('flat_rental') || p.pricePkr >= 50000)
      ) ||
      uniquePkgs.find(
        (p) => p.packageType?.includes('flat_rental') || p.pricePkr >= 50000
      );
    const flatOffPeak = uniquePkgs.find(
      (p) => p.packageType?.includes('offpeak') && p.pricePkr >= 50000
    );
    const isGenericEst = Boolean(
      !v.isClaimed ||
        !flatPeak ||
        (flatPeak as any).isEstimated ||
        /^estimated\s|^market estimated\s/i.test(String(flatPeak.name || ''))
    );
    const peakPkr = flatPeak ? Number(flatPeak.pricePkr) : Math.round(minPerHead * 180);
    const offPeakPkr = flatOffPeak
      ? Number(flatOffPeak.pricePkr)
      : Math.round(peakPkr * 0.72);
    return { peakPkr, offPeakPkr, isEstimated: isGenericEst };
  };

  const normalizeAreaName = (val?: string) =>
    (val || '').toLowerCase().replace(/\s*\/\s*/g, '/').trim();

  const requestedSortBy: string | undefined =
    searchFilter?.sortBy || blueprint?.layoutConfig?.sortBy;

  const displayVenues = useMemo(() => {
    const filtered = sourceVenues.filter((v) => {
      const matchesCorridor =
        selectedCorridors.length === 0 ||
        selectedCorridors.some((c) => {
          const vArea = normalizeAreaName(v.area);
          const target = normalizeAreaName(c);
          return vArea === target;
        });
      const matchesType =
        selectedVenueTypes.length === 0 ||
        selectedVenueTypes.some((t) => (v.venueType || '').toLowerCase() === t.toLowerCase());
      const matchesCapacity = minGuestsFilter === 0 || v.maxCapacity >= minGuestsFilter;
      return matchesCorridor && matchesType && matchesCapacity;
    });

    const baseDisplayList = isBackendFiltered
      ? sourceVenues
      : filtered.length > 0
        ? filtered
        : minGuestsFilter > 0
          ? sourceVenues
              .filter((v) => {
                const matchesCorridor =
                  selectedCorridors.length === 0 ||
                  selectedCorridors.some((c) => normalizeAreaName(v.area) === normalizeAreaName(c));
                const matchesType =
                  selectedVenueTypes.length === 0 ||
                  selectedVenueTypes.some((t) => (v.venueType || '').toLowerCase() === t.toLowerCase());
                return matchesCorridor && matchesType;
              })
              .sort((a, b) => b.maxCapacity - a.maxCapacity)
          : filtered;

    return [...baseDisplayList].sort((a, b) => {
      if (requestedSortBy) {
        if (requestedSortBy === 'price_asc') {
          return getVenuePeakHallRental(a).peakPkr - getVenuePeakHallRental(b).peakPkr;
        }
        if (requestedSortBy === 'price_desc') {
          return getVenuePeakHallRental(b).peakPkr - getVenuePeakHallRental(a).peakPkr;
        }
        if (requestedSortBy === 'capacity_desc') {
          return b.maxCapacity - a.maxCapacity;
        }
        if (requestedSortBy === 'rating_desc') {
          return b.googleRating - a.googleRating;
        }
      }
      if (isBackendFiltered) {
        return 0;
      }
      const distA = getDistanceKm(userCoords.lat, userCoords.lng, a.lat, a.lng);
      const distB = getDistanceKm(userCoords.lat, userCoords.lng, b.lat, b.lng);
      const crowdScoreA =
        (a.googleReviewCount || 0) * (a.googleRating || 4.5) - (distA !== null ? distA * 25 : 0);
      const crowdScoreB =
        (b.googleReviewCount || 0) * (b.googleRating || 4.5) - (distB !== null ? distB * 25 : 0);
      return crowdScoreB - crowdScoreA;
    });
  }, [
    sourceVenues,
    selectedCorridors,
    selectedVenueTypes,
    minGuestsFilter,
    isBackendFiltered,
    requestedSortBy,
    userCoords.lat,
    userCoords.lng
  ]);
  void onSelectCorridor;
  const focusMetrics: string[] =
    propFocusMetrics && propFocusMetrics.length > 0
      ? propFocusMetrics
      : blueprint?.layoutConfig?.focusMetrics || searchFilter?.focusMetrics || [];

  const hasSpokenContextFilter =
    hasVoiceSearchCriteria ||
    selectedCorridors.length > 0 ||
    selectedVenueTypes.length > 0 ||
    minGuestsFilter > 0 ||
    activeCardView !== 'discovery' ||
    focusMetrics.length > 0;

  const pageSize = 4;
  const effectiveLimit =
    viewportWidth >= 1680
      ? hasSpokenContextFilter
        ? renderedCount
        : 5
      : viewportWidth >= 1200
        ? hasSpokenContextFilter
          ? renderedCount
          : pageSize
        : viewportWidth >= 900
          ? hasSpokenContextFilter
            ? renderedCount
            : 3
          : renderedCount;
  const visibleVenues = displayVenues.slice(0, effectiveLimit);
  const hasMoreToRender =
    (viewportWidth < 900 || hasSpokenContextFilter) && visibleVenues.length < displayVenues.length;

  // Ensure voice-spotlighted venue is always mounted in the DOM batch
  useEffect(() => {
    if (!voiceSpotlightVenueId) return;
    const targetIdx = displayVenues.findIndex((v) => v.id === voiceSpotlightVenueId);
    if (targetIdx >= renderedCount) {
      setRenderedCount(Math.min(displayVenues.length, Math.ceil((targetIdx + 4) / 16) * 16));
    }
  }, [voiceSpotlightVenueId, displayVenues, renderedCount]);

  // Progressive infinite-scroll batching via IntersectionObserver on sentinel
  useEffect(() => {
    if (!hasMoreToRender) return;
    const sentinel = loadMoreSentinelRef.current;
    const root = gridContainerRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRenderedCount((prev) => Math.min(displayVenues.length, prev + 16));
        }
      },
      { root, rootMargin: '280px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreToRender, displayVenues.length, renderedCount]);

  const handleGridScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMoreToRender) return;
    const el = e.currentTarget;
    if (
      el.scrollTop + el.clientHeight >= el.scrollHeight - 360 ||
      el.scrollLeft + el.clientWidth >= el.scrollWidth - 360
    ) {
      setRenderedCount((prev) => Math.min(displayVenues.length, prev + 16));
    }
  };

  const handleCardClick = (venue: Venue, focusView?: 'overview' | 'packages' | 'photos' | 'amenities') => {
    const targetFocus =
      focusView ||
      (activeCardView === 'packages'
        ? 'packages'
        : activeCardView === 'amenities'
          ? 'amenities'
          : 'overview');
    onSelectVenue(venue, targetFocus);
  };

  const formatVenueType = (t?: string) => {
    if (!t) return 'Banquet Hall';
    return t
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatCompactAddress = (address: string) => {
    let clean = address
      .replace(/Naval Museum Complex,\s*/i, '')
      .replace(/\bHabib Ibrahim Rehmatullah Road\b/i, 'Habibullah Rd')
      .replace(/\bClub Road Intersection\b/i, 'Club Rd')
      .replace(/\bMain Shahrah-e-Faisal near Karsaz Flyover\b/i, 'Main Shahrah-e-Faisal')
      .replace(/\bModel Colony \/ Malir Cantt Road\b/i, 'Malir Cantt Rd')
      .replace(/\bMain Commercial Boulevard\b/i, 'Commercial Blvd')
      .replace(/\bPlot [A-Z0-9-]+,\s*/i, '')
      .replace(/\bNear\s+/i, '')
      .replace(/\bRoad\b/g, 'Rd')
      .replace(/\bAvenue\b/g, 'Ave')
      .replace(/\bStreet\b/g, 'St');
    if (clean.length > 42) {
      clean = clean.replace(/,\s*Karachi$/i, '');
    }
    return clean;
  };

  const spokenContextSummary = (() => {
    const parts: string[] = [];
    if (selectedCorridors.length > 0) parts.push(selectedCorridors.join(' & '));
    if (selectedVenueTypes.length > 0) parts.push(selectedVenueTypes.map(formatVenueType).join(' & '));
    if (minGuestsFilter > 0) parts.push(`${minGuestsFilter}+ Guests`);
    if (activeCardView === 'packages') parts.push('Hall Rental View');
    if (activeCardView === 'amenities') parts.push('Specs & Backup View');
    if (focusMetrics.length > 0 && parts.length < 2) {
      parts.push(focusMetrics.slice(0, 2).map((m) => m.replace(/_/g, ' ')).join(' • '));
    }
    if (parts.length > 0 && displayVenues.length > 0) {
      parts.push(`${displayVenues.length} venues`);
    }
    return parts.join(' • ');
  })();

  const dynamicTitle =
    !userModifiedFilter && blueprint?.title && !blueprint.title.includes('_')
      ? blueprint.title
      : selectedCorridors.length > 0
        ? `${selectedCorridors.join(' & ')}${
            selectedVenueTypes.length > 0
              ? ` — ${selectedVenueTypes.map(formatVenueType).join(' & ')}`
              : ' Venues'
          }`
        : 'Popular Venues in Karachi';

  const mobileScrollerClass = visibleVenues.length > 2 ? 'mobile-momentum-2row' : 'mobile-momentum-1row';
  const totalKarachiCount =
    allVenues && allVenues.length > 0
      ? allVenues.length
      : sourceVenues.length > 4
        ? sourceVenues.length
        : 567;
  const corridorOptions = [
    'North Nazimabad',
    'Gulistan-e-Johar',
    'Korangi',
    'Malir',
    'Nazimabad',
    'Shahrah-e-Faisal',
    'DHA/Clifton',
    'Gulshan-e-Iqbal',
    'Federal B Area',
    'Bahria Town',
    'Tariq Road',
    'Karsaz'
  ];

  return (
    <div className="stage-deck-content spotlight-viewport-deck">
      {/* Smart Relaxation Notice Banner when strict multi-area/type query was auto-relaxed */}
      {!userModifiedFilter && (relaxationNotice || searchFilter?.relaxationNotice) && (
        <div className="relaxation-banner" role="status">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
            <SparklesIcon size={14} style={{ color: 'hsl(var(--accent-gold))', flexShrink: 0 }} />
            <span>{relaxationNotice || searchFilter?.relaxationNotice}</span>
          </div>
          {onCompareVenue && displayVenues.length >= 2 && (
            <button
              type="button"
              className="corridor-chip active"
              style={{ padding: '0.2rem 0.65rem', fontSize: '0.7rem' }}
              onClick={() => onCompareVenue(displayVenues[0])}
            >
              <ChartBarIcon size={12} />
              <span>Compare Hall Rentals</span>
            </button>
          )}
        </div>
      )}

      {/* Clean Modern Header Row: Title + Dynamic Island Spoken Context Pill + Near Me Button */}
      <div className="deck-header" style={{ marginBottom: '0.4rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap', minWidth: 0 }}>
          <h2 id="stage-active-heading" data-stage-heading="true" tabIndex={-1} className="deck-title">
            {dynamicTitle}
          </h2>

          {hasSpokenContextFilter && spokenContextSummary && (
            <div className="spoken-context-pill" role="status" aria-label={`Active voice filter: ${spokenContextSummary}`}>
              <SparklesIcon size={12} style={{ color: 'hsl(var(--accent-gold))', flexShrink: 0 }} />
              <span>{spokenContextSummary}</span>
              <button
                type="button"
                className="spoken-context-pill-dismiss"
                aria-label="Clear voice filter"
                title="Reset to All Venues"
                onClick={() => {
                  runStageViewTransition(() => {
                    setSelectedCorridors([]);
                    setSelectedVenueTypes([]);
                    setMinGuestsFilter(0);
                    setActiveCardView('discovery');
                    setUserModifiedFilter(true);
                    setRenderedCount(16);
                  }, 'scene-morph');
                  if (onClearVoiceFilter) {
                    onClearVoiceFilter();
                  } else if (onTouchAction) {
                    onTouchAction('filter_corridor', { corridor: 'All' });
                  }
                }}
              >
                <CloseIcon size={10} />
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className={`corridor-chip ${userCoords.isLiveGps ? 'active' : ''}`.trim()}
          style={{ padding: '0.26rem 0.68rem', fontSize: '0.74rem', flexShrink: 0 }}
          onClick={requestLiveGps}
          aria-label="Sort by Near Me GPS location"
        >
          <MapPinIcon size={12} style={{ color: 'hsl(var(--glow-gemini-cyan))', flexShrink: 0 }} />
          <span>Near Me</span>
        </button>
      </div>

      {/* Horizontal Scrollable Corridor Filter Strip */}
      <div className="corridor-filter-strip" role="region" aria-label="Filter venues by Karachi corridor">
        <button
          type="button"
          className={`corridor-chip ${selectedCorridors.length === 0 ? 'active' : ''}`.trim()}
          onClick={() => {
            runStageViewTransition(() => {
              setSelectedCorridors([]);
              setUserModifiedFilter(true);
              setRenderedCount(16);
            }, 'scene-morph');
            if (onSelectCorridor) onSelectCorridor('All');
            if (onTouchAction) onTouchAction('filter_corridor', { corridor: 'All' });
          }}
        >
          <span>All Karachi ({totalKarachiCount})</span>
        </button>
        {corridorOptions.map((corridor) => {
          const isSelected = selectedCorridors.some(
            (c) => normalizeAreaName(c) === normalizeAreaName(corridor)
          );
          return (
            <button
              type="button"
              key={corridor}
              className={`corridor-chip ${isSelected ? 'active' : ''}`.trim()}
              onClick={() => {
                const nextCorridors = isSelected ? [] : [corridor];
                runStageViewTransition(() => {
                  setSelectedCorridors(nextCorridors);
                  setUserModifiedFilter(true);
                  setRenderedCount(16);
                }, 'scene-morph');
                const targetLabel = isSelected ? 'All' : corridor;
                if (onSelectCorridor) onSelectCorridor(targetLabel);
                if (onTouchAction) onTouchAction('filter_corridor', { corridor: targetLabel });
              }}
            >
              <span>{corridor}</span>
            </button>
          );
        })}
      </div>

      {/* Spotlight Venue Cards Grid (1-Row Desktop Grid + Mobile 1-Col Vertical Scroller) */}
      <div
        ref={gridContainerRef}
        onScroll={handleGridScroll}
        key={`${selectedCorridors.join(',')}-${activeCardView}-${minGuestsFilter}-${sortMode}`}
        className={`grid-cards spotlight-adaptive-grid ${mobileScrollerClass}`.trim()}
      >
        {visibleVenues.map((venue, idx) => {
          const { peakPkr: peakHallRentalPkr, offPeakPkr: offPeakHallRentalPkr, isEstimated } =
            getVenuePeakHallRental(venue);
          const hasExplicitHallRental = !isEstimated;

          const rawCover =
            venue.images?.[0]?.filePathWebp ||
            (venue.images?.[0] as any)?.file_path_webp ||
            `/uploads/venues/${venue.slug}/photo_0.webp`;
          const coverSrc = rawCover.includes('?') ? rawCover : `${rawCover}?v=2`;
          const distanceKm = getDistanceKm(userCoords.lat, userCoords.lng, venue.lat, venue.lng);
          const isVoiceSpotlighted = voiceSpotlightVenueId === venue.id;

          return (
            <div
              id={idx === 0 ? 'spotlight-select' : `spotlight-select-${venue.id}`}
              key={venue.id}
              className={idx >= 4 && !isVoiceSpotlighted ? 'deferred-card' : undefined}
              ref={(el) => {
                cardRefs.current[venue.id] = el;
                if (idx === visibleVenues.length - 1) {
                  loadMoreSentinelRef.current = el;
                }
              }}
              style={{ display: 'flex', minWidth: 0, height: '100%' }}
            >
              <ConicGlowCard
                className={`venue-card composable-stage-card ${isVoiceSpotlighted ? 'voice-spotlight-card' : ''}`.trim()}
                active={isVoiceSpotlighted}
                glowIntensity={isVoiceSpotlighted ? 0.92 : 0.65}
                onClick={() => handleCardClick(venue)}
                ariaLabel={`Explore ${venue.name} in ${venue.area}`}
                style={
                  {
                    '--card-index': Math.min(idx, 8),
                    width: '100%',
                    ...(viewportWidth >= 900 && idx < 4
                      ? { viewTransitionName: `venue-card-${venue.id.replace(/[^a-zA-Z0-9_-]/g, '-')}` }
                      : {})
                  } as React.CSSProperties
                }
              >
                {/* Card Media Header */}
                <div className="venue-card-media">
                  <img
                    src={coverSrc}
                    alt={venue.name}
                    className="venue-card-img"
                    loading={idx < 4 ? 'eager' : 'lazy'}
                    decoding="async"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div className="venue-card-scrim" />

                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, hsl(var(--obsidian-850)), hsl(var(--obsidian-950)))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'hsl(var(--text-muted))',
                      fontSize: '0.85rem'
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{venue.name}</span>
                  </div>

                  {/* Top Left: Real GPS Distance (only if granted) & Venue Type */}
                  <div className="badge-overlay-top-left">
                    <span className="pill-badge num-tabular" style={{ padding: '0.18rem 0.55rem', fontSize: '0.68rem' }}>
                      <MapPinIcon size={11} style={{ color: 'hsl(var(--glow-gemini-cyan))' }} />
                      <span>
                        {distanceKm !== null
                          ? `${distanceKm} km • ${formatVenueType(venue.venueType)}`
                          : formatVenueType(venue.venueType)}
                      </span>
                    </span>
                  </div>

                  {/* Top Right: Claimed / Popular Status */}
                  <div className="badge-overlay-top-right">
                    {venue.isClaimed ? (
                      <span className="pill-badge verified" style={{ padding: '0.18rem 0.55rem', fontSize: '0.68rem' }}>
                        <ShieldCheckIcon size={11} />
                        <span>Verified</span>
                      </span>
                    ) : (
                      <span className="pill-badge pricing" style={{ padding: '0.18rem 0.55rem', fontSize: '0.68rem' }}>
                        <FlameIcon size={11} />
                        <span>Popular</span>
                      </span>
                    )}
                  </div>

                  {/* Bottom Overlay: Corridor & Pure Line Star Rating */}
                  <div className="badge-overlay-bottom">
                    <span
                      style={{
                        fontSize: '0.66rem',
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        color: 'hsl(var(--accent-gold))',
                        letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {venue.area}
                    </span>
                    <span
                      className="pill-badge num-tabular"
                      style={{ padding: '0.16rem 0.48rem', fontSize: '0.68rem', flexShrink: 0 }}
                    >
                      <StarIcon size={11} style={{ color: 'hsl(var(--accent-gold))' }} />
                      <span>
                        {venue.googleRating.toFixed(1)} ({venue.googleReviewCount.toLocaleString()})
                      </span>
                    </span>
                  </div>
                </div>

                <div className="venue-card-body">
                  <div>
                    <h3
                      className="venue-card-title"
                      style={{
                        fontSize: '1.04rem',
                        fontWeight: 800,
                        fontFamily: 'var(--font-display)',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.25,
                        marginBottom: '0.2rem',
                        color: 'hsl(var(--text-primary))',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {venue.name}
                    </h3>

                    <p
                      className="venue-card-address"
                      title={venue.address}
                      style={{
                        fontSize: '0.74rem',
                        color: 'hsl(var(--text-secondary))',
                        lineHeight: 1.3,
                        marginBottom: '0.15rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.28rem'
                      }}
                    >
                      <MapPinIcon size={12} style={{ color: 'hsl(var(--glow-gemini-blue))', flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formatCompactAddress(venue.address)}
                      </span>
                    </p>
                  </div>

                  {/* Clean Spacious Spec Row: Guest Capacity & Off-Peak / Weekday Rate */}
                  <div key={`slot-${activeCardView}`} className="card-slot-morph venue-card-slot-stack">
                    <div
                      className="venue-card-chips-row"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.45rem',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <span
                        className="amenity-chip num-tabular"
                        style={{
                          color: 'hsl(var(--text-primary))',
                          padding: '0.2rem 0.56rem',
                          fontSize: '0.7rem',
                          flexShrink: 0
                        }}
                      >
                        <UsersIcon size={12} style={{ color: 'hsl(var(--glow-gemini-blue))', flexShrink: 0 }} />
                        <span>
                          {venue.minCapacity}–{venue.maxCapacity} Guests
                        </span>
                      </span>

                      <span
                        className="venue-hall-rental-row num-tabular"
                        style={{
                          fontSize: '0.69rem',
                          color: 'hsl(var(--text-secondary))',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Off-Peak:{' '}
                        <strong style={{ color: 'hsl(var(--text-primary))', fontWeight: 700 }}>
                          {hasExplicitHallRental
                            ? `Rs. ${offPeakHallRentalPkr.toLocaleString()}`
                            : `~Rs. ${offPeakHallRentalPkr.toLocaleString()}`}
                        </strong>
                      </span>
                    </div>
                  </div>

                  {/* Clean Minimal Hall Rental Footer (Full Card Clickable Everywhere) */}
                  <div
                    className="venue-card-footer"
                    style={{
                      borderTop: '1px solid hsl(var(--porcelain-50) / 0.07)',
                      paddingTop: '0.48rem',
                      marginTop: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span className="venue-card-footer-label" style={{ fontSize: '0.72rem', fontWeight: 500, color: 'hsl(var(--text-muted))' }}>
                      {hasExplicitHallRental ? 'Peak Hall Rental' : 'Est. Peak Rental'}
                    </span>
                    <span
                      className="pill-badge pricing num-tabular venue-card-footer-price"
                      style={{
                        fontSize: '0.74rem',
                        fontWeight: 600,
                        padding: '0.16rem 0.56rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.32rem'
                      }}
                    >
                      <span>
                        {hasExplicitHallRental
                          ? `Rs. ${peakHallRentalPkr.toLocaleString()}`
                          : `~Rs. ${peakHallRentalPkr.toLocaleString()}`}
                      </span>
                      <ArrowRightIcon size={11} />
                    </span>
                  </div>
                </div>
              </ConicGlowCard>
            </div>
          );
        })}
      </div>
    </div>
  );
});

